import { Container } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';

import { instanceApis } from '../../../apis/instanceApis';
import { solrApis } from '../../../apis/solrApis';
import AlertMessage from '../../../components/elements/alert-message/AlertMessage';
import Loading from '../../../components/elements/loading/Loading';
import SettingsTabs from '../../../components/page-sections/settings/SettingsTabs';
import Breadcrumbs from '../../../components/sections/breadcrumbs/Breadcrumbs';
import TitlePanel from '../../../components/sections/title-panel/TitlePanel';
import { useSolrInstance } from '../../../contexts/SolrContext';
import useToast, { TOAST_TYPE } from '../../../hooks/useToast';
import { buildPath, PRIVATE_ROUTE } from '../../../routes/routes';
import { sentenceCase } from '../../../utils/utils';

// Page
const SolrSettingsPage = () => {
  const navigate = useNavigate();
  const { instance, refreshInstance, loading } = useSolrInstance();
  const { projectId, instanceId } = useParams();
  const { showToast } = useToast();
  const { t } = useTranslation();

  const handleDeleteInstance = async () => {
    try {
      await solrApis.deleteSolrInstance(projectId, instanceId);

      showToast(
        TOAST_TYPE.SUCCESS,
        sentenceCase(
          t('deleteSuccess', {
            ns: 'notifications',
            text: t('instance', { ns: 'instance' }),
          })
        )
      );
      const projectOverview = buildPath({
        path: PRIVATE_ROUTE.PROJECT_OVERVIEW,
        params: { projectId: projectId },
      });
      setTimeout(() => navigate(projectOverview), 800);
    } catch (errors) {
      const { data: errorRes } = errors.response;
      if (errorRes.status === 400) {
        showToast(
          TOAST_TYPE.ERROR,
          t('crawlJobRunningError', { ns: 'notifications' })
        );
      } else {
        showToast(
          TOAST_TYPE.ERROR,
          sentenceCase(
            t('deleteError', {
              ns: 'notifications',
              text: t('instance', { ns: 'instance' }),
            })
          )
        );
      }
    }
  };

  const handleRemoveImage = async () => {
    await instanceApis.deleteInstanceImage(projectId, instanceId);
  };

  const handleUpdateCidrs = async (cidrList) => {
    const requestBody = {
      name: instance.name,
      description: instance.description,
      subdomain: instance.subdomain,
      clusterType: instance.clusterType,
      cidrs: cidrList,
    };

    await solrApis.updateInstance(projectId, instanceId, requestBody);
    await refreshInstance();
  };

  const handleUpdateInstance = async (data) => {
    if (data.icon) {
      const { icon, ...form } = data;
      const formData = new FormData();
      formData.append('image', icon);
      await instanceApis.updateInstanceImage(projectId, instanceId, formData);
      await solrApis.updateInstance(projectId, instanceId, form);
    } else {
      await solrApis.updateInstance(projectId, instanceId, data);
    }
    await refreshInstance();
  };

  if (loading) return <Loading />;

  if (!instance)
    return (
      <AlertMessage
        severity="error"
        message={sentenceCase(
          t('loading', {
            ns: 'errors',
            content: t('settingsPage', {
              ns: 'project',
              text: t('instance', { ns: 'instance' }),
            }),
          })
        )}
      />
    );

  return (
    <Container>
      <Breadcrumbs instanceName={instance.name} />
      <TitlePanel title={t('settings', { ns: 'titles' })} />
      <SettingsTabs
        instance={instance}
        onDeleteInstance={handleDeleteInstance}
        onRemoveImage={handleRemoveImage}
        onUpdateCidrs={handleUpdateCidrs}
        onUpdateInstance={handleUpdateInstance}
      />
    </Container>
  );
};

export default SolrSettingsPage;
